
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, startOfDay, startOfWeek, startOfMonth, endOfDay, endOfWeek, endOfMonth } from "date-fns";
import { fr } from "date-fns/locale";
import { Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AddExpenseDialog } from "../dialogs/AddExpenseDialog";
import { toast } from "sonner";
import { useOwnerCurrency } from "@/hooks/useOwnerCurrency";

export const MyExpensesTab = () => {
  const [showAddExpense, setShowAddExpense] = useState(false);
  const { data: currency } = useOwnerCurrency();

  const { data: expenses = [], isLoading, error } = useQuery({
    queryKey: ["expenses"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Vous devez être connecté pour voir vos dépenses");
        throw new Error("Non authentifié");
      }

      // Récupérer les données du propriétaire pour obtenir l'owner_id
      const { data: ownerData, error: ownerError } = await supabase
        .from("owner_data")
        .select("owner_id")
        .eq("owner_id", user.id)
        .single();

      if (ownerError) {
        toast.error("Erreur lors de la récupération des données du propriétaire");
        throw ownerError;
      }

      const { data, error } = await supabase
        .from("poultry_transactions")
        .select("*")
        .eq("transaction_type", "expense")
        .eq("created_by", ownerData.owner_id)
        .order("transaction_date", { ascending: false });

      if (error) {
        toast.error("Erreur lors du chargement des dépenses");
        throw error;
      }

      return data || [];
    }
  });

  if (error) {
    return (
      <div className="text-center p-4">
        <p className="text-red-500">Une erreur est survenue lors du chargement des dépenses</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="text-center p-4">
        <p>Chargement de vos dépenses...</p>
      </div>
    );
  }

  const calculateTotal = (startDate: Date, endDate: Date) => {
    return expenses
      .filter(expense => {
        const expenseDate = new Date(expense.transaction_date);
        return expenseDate >= startDate && expenseDate <= endDate;
      })
      .reduce((sum, expense) => sum + (expense.total_amount || 0), 0);
  };

  const now = new Date();
  const dailyTotal = calculateTotal(startOfDay(now), endOfDay(now));
  const weeklyTotal = calculateTotal(startOfWeek(now, { locale: fr }), endOfWeek(now, { locale: fr }));
  const monthlyTotal = calculateTotal(startOfMonth(now), endOfMonth(now));

  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString()} ${currency || 'FCFA'}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold">Mes Dépenses</h3>
        <Button onClick={() => setShowAddExpense(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle dépense
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Total Journalier</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatCurrency(dailyTotal)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Total Hebdomadaire</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatCurrency(weeklyTotal)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Total Mensuel</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatCurrency(monthlyTotal)}</p>
          </CardContent>
        </Card>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left pb-4">Date</th>
                <th className="text-left pb-4">Nom</th>
                <th className="text-left pb-4">Catégorie</th>
                <th className="text-left pb-4">Sous-catégorie</th>
                <th className="text-left pb-4">Source</th>
                <th className="text-right pb-4">Montant</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((expense) => (
                <tr key={expense.id} className="border-b hover:bg-gray-50">
                  <td className="py-4">{format(new Date(expense.transaction_date), 'dd/MM/yyyy')}</td>
                  <td className="py-4">{expense.notes?.split(' - ')[0]}</td>
                  <td className="py-4">{expense.expense_category}</td>
                  <td className="py-4">
                    {expense.expense_subcategory?.split('_')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ')}
                  </td>
                  <td className="py-4">
                    {expense.is_deducted_from_revenue ? 
                      "Chiffre d'affaires" : 
                      "Argent externe"}
                  </td>
                  <td className="py-4 text-right font-medium">
                    {formatCurrency(expense.total_amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AddExpenseDialog open={showAddExpense} onOpenChange={setShowAddExpense} />
    </div>
  );
};
